/**
 * UTM Attribution Storage Utilities
 * Captures UTM params from URL and stores in browser storage for signup attribution.
 * First-touch only — does not overwrite if already stored.
 */

const UTM_KEY = 'utm_attribution';

/**
 * Read UTM params from current URL + referrer/landingPage.
 * Returns object with fields or null if no UTM params present.
 */
function parseUtmFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const utm = {
    utmSource:   params.get('utm_source')   || '',
    utmMedium:   params.get('utm_medium')   || '',
    utmCampaign: params.get('utm_campaign') || '',
    utmTerm:     params.get('utm_term')     || '',
    utmContent:  params.get('utm_content')  || '',
    referrer:    document.referrer           || '',
    landingPage: window.location.href        || '',
  };
  // Only return if at least one UTM param is present
  if (utm.utmSource || utm.utmMedium || utm.utmCampaign) {
    return utm;
  }
  return null;
}

/**
 * Capture UTM params from URL and store in sessionStorage.
 * Used on the form signup path (EmployerSignUp).
 * First-touch: does not overwrite if already stored in this session.
 */
export function captureUtmToSessionStorage() {
  try {
    if (sessionStorage.getItem(UTM_KEY)) return; // first-touch — already captured
    const utm = parseUtmFromUrl();
    if (utm) {
      sessionStorage.setItem(UTM_KEY, JSON.stringify(utm));
    }
  } catch (err) {
    // sessionStorage unavailable (private browsing, etc.) — silently skip
    console.warn('[utmStorage] sessionStorage write failed:', err);
  }
}

/**
 * Read UTM params from sessionStorage.
 * Returns parsed object or null.
 */
export function readUtmFromSessionStorage() {
  try {
    const raw = sessionStorage.getItem(UTM_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (err) {
    console.warn('[utmStorage] sessionStorage read failed:', err);
    return null;
  }
}

/**
 * Capture UTM params from URL and store in localStorage.
 * Used before OAuth redirect (SignIn.jsx) since redirect destroys URL params.
 * First-touch: does not overwrite if already stored.
 */
export function captureUtmToLocalStorage() {
  try {
    if (localStorage.getItem(UTM_KEY)) return; // first-touch — already captured
    const utm = parseUtmFromUrl();
    if (utm) {
      localStorage.setItem(UTM_KEY, JSON.stringify(utm));
    }
  } catch (err) {
    console.warn('[utmStorage] localStorage write failed:', err);
  }
}

/**
 * Read UTM params from localStorage.
 * Returns parsed object or null.
 */
export function readUtmFromLocalStorage() {
  try {
    const raw = localStorage.getItem(UTM_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (err) {
    console.warn('[utmStorage] localStorage read failed:', err);
    return null;
  }
}

/**
 * Clear UTM params from both storages.
 * Call after successful attribution capture to prevent stale data on future logins.
 */
export function clearUtmStorage() {
  try { sessionStorage.removeItem(UTM_KEY); } catch (_) {}
  try { localStorage.removeItem(UTM_KEY); } catch (_) {}
}
